import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { GET_ALL_ADMINS, UPDATE_ADMIN_PROFILE, DELETE_ADMIN, CREATE_ADMIN } from '../apollo/queries/adminQueries';
import './css/AdminProfilePage.css';

const AdminProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    email: '',
    mobile: '',
    phone: '',
    department: '',
    company: ''
  });
  const [createFormData, setCreateFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobile: '',
    phone: '',
    department: '',
    company: '',
    role: 'ADMIN'
  });

  const { data: adminsData, loading, error, refetch } = useQuery(GET_ALL_ADMINS);
  const [updateAdminProfile] = useMutation(UPDATE_ADMIN_PROFILE);
  const [deleteAdmin] = useMutation(DELETE_ADMIN);
  const [createAdmin] = useMutation(CREATE_ADMIN);

  const admins = adminsData?.getAllAdmins || [];

  const handleMenuClick = (path) => {
    navigate(path);
  };

  // 체크박스 선택 핸들러
  const handleSelectAdmin = (adminId) => {
    setSelectedAdmins(prev => 
      prev.includes(adminId) 
        ? prev.filter(id => id !== adminId)
        : [...prev, adminId]
    );
  };

  // 전체 선택 핸들러
  const handleSelectAll = () => {
    if (selectedAdmins.length === admins.length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(admins.map(admin => admin.id));
    }
  };

  // 수정 버튼 클릭
  const handleEditClick = (admin) => {
    setEditingAdmin(admin.id);
    setFormData({
      id: admin.id,
      name: admin.name || '',
      email: admin.email || '',
      mobile: admin.mobile || '',
      phone: admin.phone || '',
      department: admin.department || '',
      company: admin.company || ''
    });
  };

  // 폼 데이터 변경
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 생성 폼 데이터 변경
  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 계정 추가 모달 열기
  const handleAddAccountClick = () => {
    setShowCreateModal(true);
    setCreateFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      mobile: '',
      phone: '',
      department: '',
      company: '',
      role: 'ADMIN'
    });
  };

  // 계정 추가 모달 닫기
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      mobile: '',
      phone: '',
      department: '',
      company: '',
      role: 'ADMIN'
    });
  };

  // 관리자 계정 생성
  const handleCreateAdmin = async () => {
    // 유효성 검사
    if (!createFormData.name.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }
    if (!createFormData.email.trim()) {
      alert('이메일을 입력해주세요.');
      return;
    }
    if (!createFormData.password.trim()) {
      alert('비밀번호를 입력해주세요.');
      return;
    }
    if (createFormData.password !== createFormData.confirmPassword) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (createFormData.password.length < 6) {
      alert('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    try {
      const result = await createAdmin({
        variables: {
          input: {
            name: createFormData.name.trim(),
            email: createFormData.email.trim(),
            password: createFormData.password,
            mobile: createFormData.mobile.trim() || null,
            phone: createFormData.phone.trim() || null,
            department: createFormData.department.trim() || null,
            company: createFormData.company.trim() || null,
            role: createFormData.role
          }
        }
      });

      if (result.data.createAdmin.success) {
        alert('관리자 계정이 성공적으로 생성되었습니다.');
        handleCloseCreateModal();
        refetch();
      } else {
        alert(result.data.createAdmin.message);
      }
    } catch (error) {
      console.error('관리자 생성 오류:', error);
      alert('관리자 계정 생성 중 오류가 발생했습니다.');
    }
  };

  // 프로필 업데이트
  const handleUpdateProfile = async () => {
    try {
      const result = await updateAdminProfile({
        variables: {
          input: formData
        }
      });

      if (result.data.updateAdminProfile.success) {
        alert('관리자 정보가 성공적으로 수정되었습니다.');
        setEditingAdmin(null);
        refetch();
      } else {
        alert(result.data.updateAdminProfile.message);
      }
    } catch (error) {
      console.error('프로필 수정 오류:', error);
      alert('프로필 수정 중 오류가 발생했습니다.');
    }
  };

  // 선택된 관리자 삭제
  const handleDeleteSelected = async () => {
    if (selectedAdmins.length === 0) {
      alert('삭제할 관리자를 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택된 ${selectedAdmins.length}명의 관리자를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      for (const adminId of selectedAdmins) {
        await deleteAdmin({
          variables: { id: adminId.toString() }
        });
      }
      alert('선택된 관리자가 삭제되었습니다.');
      setSelectedAdmins([]);
      refetch();
    } catch (error) {
      console.error('관리자 삭제 오류:', error);
      alert('관리자 삭제 중 오류가 발생했습니다.');
    }
  };

  // 등록일 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit'
    });
  };

  if (loading) return <div className="loading">로딩 중...</div>;
  if (error) return <div className="error">오류가 발생했습니다: {error.message}</div>;

  return (
    <>
      <div className="breadcrumb">
        <span>홈 &gt; 환경 설정 &gt; 관리자 정보수정</span>
      </div>
      <div className="admin-profile-page">
        <div className="admin-profile-container">
        <aside className="admin-sidebar">
          <div className="sidebar-menu">
            <h3>환경 설정</h3>
            <ul>
              <li 
                className={location.pathname === '/admin/settings' ? 'active' : ''}
                onClick={() => handleMenuClick('/admin/settings')}
              >
                기본 환경 설정
              </li>
              <li 
                className={location.pathname === '/admin/profile' ? 'active' : ''}
                onClick={() => handleMenuClick('/admin/profile')}
              >
                관리자 정보수정
              </li>
            </ul>
          </div>
        </aside>

        <main className="admin-content">
          <div className="content-header">
            <h2>관리자 정보수정</h2>
            <div className="content-actions">
              <button className="btn-add-admin" onClick={handleAddAccountClick}>
                계정 추가
              </button>
            </div>
          </div>

          <div className="admin-table-section">
            <div className="table-header">
              <div className="table-info">
                <span>총 인원 : {admins.length} 명</span>
              </div>
            </div>

            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>
                      <input 
                        type="checkbox" 
                        checked={selectedAdmins.length === admins.length && admins.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>선택</th>
                    <th>아이디</th>
                    <th>e-mail</th>
                    <th>담당자</th>
                    <th>담당자 번호</th>
                    <th>등록 일자</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedAdmins.includes(admin.id)}
                          onChange={() => handleSelectAdmin(admin.id)}
                        />
                      </td>
                      <td>선택</td>
                      <td>
                        {editingAdmin === admin.id ? (
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="edit-input"
                          />
                        ) : (
                          admin.name || '-'
                        )}
                      </td>
                      <td>
                        {editingAdmin === admin.id ? (
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="edit-input"
                          />
                        ) : (
                          admin.email || '-'
                        )}
                      </td>
                      <td>
                        {editingAdmin === admin.id ? (
                          <input
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            className="edit-input"
                          />
                        ) : (
                          admin.department || '-'
                        )}
                      </td>
                      <td>
                        {editingAdmin === admin.id ? (
                          <input
                            type="text"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            className="edit-input"
                          />
                        ) : (
                          admin.mobile || admin.phone || '-'
                        )}
                      </td>
                      <td>{formatDate(admin.createdAt)}</td>
                      <td>
                        {editingAdmin === admin.id ? (
                          <div className="edit-actions">
                            <button onClick={handleUpdateProfile} className="btn-save">저장</button>
                            <button onClick={() => setEditingAdmin(null)} className="btn-cancel">취소</button>
                          </div>
                        ) : (
                          <button onClick={() => handleEditClick(admin)} className="btn-edit">수정</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-actions">
              <button onClick={handleDeleteSelected} className="btn-delete">선택 삭제</button>
            </div>

            <div className="table-footer">
              <button className="btn-save-all">저장</button>
            </div>
          </div>
        </main>
      </div>

      {/* 계정 추가 모달 */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseCreateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>관리자 계정 추가</h3>
              <button className="modal-close" onClick={handleCloseCreateModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>이름 *</label>
                <input
                  type="text"
                  name="name"
                  value={createFormData.name}
                  onChange={handleCreateInputChange}
                  placeholder="이름을 입력하세요"
                  required
                />
              </div>
              <div className="form-group">
                <label>이메일 *</label>
                <input
                  type="email"
                  name="email"
                  value={createFormData.email}
                  onChange={handleCreateInputChange}
                  placeholder="이메일을 입력하세요"
                  required
                />
              </div>
              <div className="form-group">
                <label>비밀번호 *</label>
                <input
                  type="password"
                  name="password"
                  value={createFormData.password}
                  onChange={handleCreateInputChange}
                  placeholder="비밀번호를 입력하세요 (6자 이상)"
                  required
                />
              </div>
              <div className="form-group">
                <label>비밀번호 확인 *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={createFormData.confirmPassword}
                  onChange={handleCreateInputChange}
                  placeholder="비밀번호를 다시 입력하세요"
                  required
                />
              </div>
              <div className="form-group">
                <label>휴대전화</label>
                <input
                  type="text"
                  name="mobile"
                  value={createFormData.mobile}
                  onChange={handleCreateInputChange}
                  placeholder="휴대전화번호를 입력하세요"
                />
              </div>
              <div className="form-group">
                <label>전화번호</label>
                <input
                  type="text"
                  name="phone"
                  value={createFormData.phone}
                  onChange={handleCreateInputChange}
                  placeholder="전화번호를 입력하세요"
                />
              </div>
              <div className="form-group">
                <label>부서</label>
                <input
                  type="text"
                  name="department"
                  value={createFormData.department}
                  onChange={handleCreateInputChange}
                  placeholder="부서를 입력하세요"
                />
              </div>
              <div className="form-group">
                <label>회사</label>
                <input
                  type="text"
                  name="company"
                  value={createFormData.company}
                  onChange={handleCreateInputChange}
                  placeholder="회사명을 입력하세요"
                />
              </div>
              <div className="form-group">
                <label>권한 *</label>
                <select
                  name="role"
                  value={createFormData.role}
                  onChange={handleCreateInputChange}
                  required
                >
                  <option value="ADMIN">관리자</option>
                  <option value="SUPER_ADMIN">슈퍼 관리자</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseCreateModal}>취소</button>
              <button className="btn-create" onClick={handleCreateAdmin}>생성</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AdminProfilePage; 